import React from 'react';
import { View, Text, StyleSheet, Platform, ViewStyle } from 'react-native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { transformFeeStructureDollarSign } from '@/utils/residents/paymentUtils';
import type { FeeStructure } from '@/types/residents/payment';
import { PaymentSectionHeader } from './PaymentSectionHeader';

type Variant = 'desktop' | 'mobile';

interface ConvenienceFeesSectionProps {
  /** Fee structure with credit, debit, and bank account fees */
  feeStructure: FeeStructure;
  /** @deprecated Use feeStructure instead */
  feeStructureDisplay?: FeeStructure;
  /** Display variant - affects styling */
  variant?: Variant;
  /** Whether to show the header */
  showHeader?: boolean;
  /** Custom container style */
  style?: ViewStyle;
}

/**
 * Unified Convenience Fees Display Component
 * Shows fee rates for Credit, Debit, and ACH payment methods
 * Supports both mobile and desktop styling variants
 */
export const ConvenienceFeesSection: React.FC<ConvenienceFeesSectionProps> = ({
  feeStructure,
  feeStructureDisplay, // deprecated - for backward compatibility
  variant = Platform.OS === 'web' ? 'desktop' : 'mobile',
  showHeader = true,
  style,
}) => {
  // Support both prop names for backward compatibility
  const fees = feeStructure || feeStructureDisplay || {};
  const isMobile = variant === 'mobile';

  const formatFeeValue = (value?: string): string => {
    if (!value) return '-';
    return transformFeeStructureDollarSign(value);
  };

  return (
    <View style={[
      styles.container,
      isMobile ? styles.containerMobile : styles.containerDesktop,
      style,
    ]}>
      {showHeader && (
        isMobile ? (
          <View style={styles.headerRowMobile}>
            <Text style={styles.headerTextMobile}>Convenience fees</Text>
          </View>
        ) : (
          <PaymentSectionHeader title="Convenience fees" />
        )
      )}
      
      <View style={isMobile ? styles.feesContentMobile : undefined}>
        <View style={[styles.feeRow, isMobile && styles.feeRowMobile]}>
          <Text style={isMobile ? styles.feeLabelMobile : styles.feeLabelDesktop}>Credit</Text>
          <Text style={isMobile ? styles.feeValueMobile : styles.feeValueDesktop}>
            {formatFeeValue(fees.creditCard)}
          </Text>
        </View>
        
        <View style={[styles.feeRow, isMobile && styles.feeRowMobile]}>
          <Text style={isMobile ? styles.feeLabelMobile : styles.feeLabelDesktop}>Debit</Text>
          <Text style={isMobile ? styles.feeValueMobile : styles.feeValueDesktop}>
            {formatFeeValue(fees.debitCard)}
          </Text>
        </View>
        
        <View style={[styles.feeRow, isMobile && styles.feeRowMobile]}>
          <Text style={isMobile ? styles.feeLabelMobile : styles.feeLabelDesktop}>ACH</Text>
          <Text style={isMobile ? styles.feeValueMobile : styles.feeValueDesktop}>
            {formatFeeValue(fees.bankAccount)}
          </Text>
        </View>
      </View>
    </View>
  );
};

// Alias for backward compatibility with mobile imports
export const ConvenienceFeesDisplay = ConvenienceFeesSection;

const styles = StyleSheet.create({
  // Container
  container: {},
  containerDesktop: {
    paddingVertical: 14,
  },
  containerMobile: {
    paddingVertical: 24,
    paddingHorizontal: 24,
    borderTopWidth: 1,
    borderTopColor: MARKETPLACE_COLORS.dividerGray,
  },
  
  // Header (mobile)
  headerRowMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  headerTextMobile: {
    fontSize: 14,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  
  // Fee rows
  feeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  feeRowMobile: {
    // Mobile-specific row styling
  },
  
  // Mobile content wrapper
  feesContentMobile: {
    paddingLeft: 16,
    gap: 4,
  },
  
  // Desktop labels/values
  feeLabelDesktop: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 18,
    paddingVertical: 8,
  },
  feeValueDesktop: {
    color: '#10B981',
    fontSize: 18,
    fontWeight: '700',
    paddingVertical: 8,
  },
  
  // Mobile labels/values
  feeLabelMobile: {
    fontSize: 14,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  feeValueMobile: {
    fontSize: 14,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
});
